import { transaction } from '@src/slide/domain/slide-app';
import { AttachmentInfo } from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    isAttachmentImageElement,
    isAudioElement, isShapeModel,
    isVideoElement,
} from '@src/slide/domain/ui-model/elements/element-judge';
import { GraphType } from '@src/slide/infrastructure/module/dependent/shape-info/constant';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    AUDIO_DEFAULT_IMG,
    AttachmentClassify,
    DEFAULT_AUDIO_HEIGHT,
    DEFAULT_AUDIO_WIDTH,
    DEFAULT_CARD_HEIGHT,
    DEFAULT_CARD_WIDTH,
    EShowType,
    VIDEO_DEFAULT_IMG, ShowTypeTrans,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import { IShapeModel, ShapeSubType } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { getAttachmentInfo } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/attachment';
import { getAspectRatioFromSrc, getDefaultImageSizeByRatio, getDefaultVideoSizeByRatio } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/size';
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import i18n from '@i18next/i18n';
import { ISnackbarProps } from '@tencent/dui/lib/components/Snackbar/interface';

const DEFAULT_POS = 100;

type TipCallback = (msg: string, type: ISnackbarProps['type']) => void;

export class AttachmentSwitcher {
    public static switch(currentModel: IShapeModel | IPictureModel, showType: EShowType, tipCallback?: TipCallback) {
        if (AttachmentSwitcher.isSwitchToShapeModel(currentModel, showType)) {
            return AttachmentSwitcher.switchToShapeModel(currentModel, showType, tipCallback);
        }

        if (AttachmentSwitcher.isSwitchToPictureModel(currentModel, showType)) {
            return AttachmentSwitcher.switchToPictureModel(currentModel as IShapeModel, showType, tipCallback);
        }
    }

    private static isSwitchToShapeModel(currentModel: IShapeModel | IPictureModel, showType: EShowType) {
        return [EShowType.TEXT, EShowType.SIMPLE_TEXT, EShowType.CARD].includes(showType);
    }

    private static isSwitchToPictureModel(currentModel: IShapeModel | IPictureModel, showType: EShowType) {
        return currentModel.type === UIModelType.SHAPE && showType === EShowType.PREVIEW;
    }

    /**
     * 切换为 shapeModel，文字链/图标文字链/卡片态
     * @param model
     * @param showType
     * @param tipCallback
     */
    private static switchToShapeModel(
        model: IShapeModel | IPictureModel,
        showType: EShowType,
        tipCallback?: TipCallback,
    ) {
        const slideModel = model.slideRef as ISlideModel;
        const attachment: AttachmentInfo = {
            ...getAttachmentInfo(model),
            ...this.getPosterInfo(model),
            showType,
        };
        const { left, top } = model.props?.style || {};
        const props = this.getShapeModelProps(attachment, left, top, showType);
        const extraInfo = this.getShapeModelExtraInfo(model, attachment);
        tipCallback && this.handleChangeTips(model, showType, tipCallback);
        this.changeElementAndSelect(slideModel, model, [[UIModelType.SHAPE], [props], [extraInfo]]);
    }

    /**
     * 切换为 pictureModel，视频/音频/图片的预览
     * @param model
     * @param showType
     * @param tipCallback
     */
    private static switchToPictureModel(model: IShapeModel, showType: EShowType, tipCallback?: TipCallback) {
        const slideModel = model.slideRef as ISlideModel;
        const { left, top } = model.props?.style || {};
        const attachment = {
            ...model.props.attachment,
            showType,
        };
        const props = this.getImageModelProps(attachment, left, top);
        const extraInfo = this.getImageModelExtraInfo(attachment);
        tipCallback && this.handleChangeTips(model, showType, tipCallback);
        this.changeElementAndSelect(slideModel, model, [[UIModelType.IMAGE], [props], [extraInfo]]);
    }

    /**
     * 删除旧元素
     * 添加新元素并选中
     * @param slideModel
     * @param oldElement
     * @param addElementParams
     */
    private static async changeElementAndSelect(
        slideModel: ISlideModel, oldElement: IBaseModel,
        addElementParams: [any, any, any],
    ) {
        // 如果是从文字链/图标+文字链/卡片态切换到音频/视频态，会有 mediaDuration 存在， 需要重新添加默认动画（默认其导入的时候就有一条动画）
        let mediaDuration;
        const [newElementExtraInfo] = addElementParams[2];
        if ((newElementExtraInfo.video || newElementExtraInfo.audio) && isShapeModel(oldElement)) {
            mediaDuration = await oldElement?.getAttachmentManager()?.getMediaDuration({
                objKey: oldElement.props.attachment?.objKey,
                classify: oldElement.props.attachment?.classify,
            });
        }

        transaction(() => {
            // 由于这两步包在 transaction 中，也就是只有这两步执行完成之后才会执行 sdkOnDidChange 回调
            // 导致 addElement 执行时 slideModel 的 children 还没更新，所以 addElement 内部的选中逻辑就错乱了
            slideModel.deleteElement([oldElement]);
            slideModel.addElement(...addElementParams);

            // 如果是从文字链/图标+文字链/卡片态切换到音频/视频态，会有 mediaDuration 存在， 需要重新添加默认动画（默认其导入的时候就有一条动画）
            if (mediaDuration) {
                const attachmentManager = oldElement?.getAttachmentManager();
                newElementExtraInfo.video && attachmentManager?.addAnimationToVideo(slideModel, mediaDuration);
                newElementExtraInfo.audio && attachmentManager?.addAnimationToAudio(slideModel, mediaDuration);
            }
        });

        // 数据变更完成之后，选中新增加的元素
        const newModel = slideModel.children[slideModel.children.length - 1];
        slideModel.selectionService?.selectedElements.clear();
        slideModel.selectionService?.selectedElements.add(newModel);
    }

    private static getImageModelProps(attachment: AttachmentInfo, left: number, top: number) {
        const defaultStyle = {
            bgShape: GraphType.RECT,
            left: left || DEFAULT_POS,
            top: top || DEFAULT_POS,
        };
        switch (attachment.classify) {
            case AttachmentClassify.VIDEO: {
                const aspectRatio = getAspectRatioFromSrc(attachment.posterInfo?.url);
                const { width, height } = getDefaultVideoSizeByRatio(aspectRatio);
                return {
                    src: attachment.posterInfo?.url || VIDEO_DEFAULT_IMG,
                    style: {
                        ...defaultStyle,
                        width,
                        height,
                    },
                };
            }
            case AttachmentClassify.AUDIO: {
                return {
                    src: AUDIO_DEFAULT_IMG,
                    style: {
                        ...defaultStyle,
                        width: DEFAULT_AUDIO_WIDTH,
                        height: DEFAULT_AUDIO_HEIGHT,
                    },
                };
            }
            case AttachmentClassify.IMAGE: {
                const aspectRatio = getAspectRatioFromSrc(attachment.posterInfo?.url);
                const { width, height } = getDefaultImageSizeByRatio(aspectRatio);
                return {
                    attachment,
                    src: attachment.posterInfo?.url,
                    style: {
                        ...defaultStyle,
                        width,
                        height,
                    },
                };
            }
        }
    }

    private static getImageModelExtraInfo(attachment: AttachmentInfo) {
        const { objKey, type, size } = attachment;
        switch (attachment.classify) {
            case AttachmentClassify.VIDEO: {
                return {
                    video: {
                        link: objKey,
                        format: type,
                        size,
                    },
                    name: attachment.name,
                };
            }
            case AttachmentClassify.AUDIO: {
                return {
                    audio: {
                        link: objKey,
                        format: type,
                        size,
                    },
                    name: attachment.name,
                };
            }
            case AttachmentClassify.IMAGE: {
                return {
                    name: attachment.name,
                };
            }
        }
    }

    private static getShapeModelProps(attachment: AttachmentInfo, left: number, top: number, showType: EShowType) {
        if (showType === EShowType.TEXT || showType === EShowType.SIMPLE_TEXT) {
            return {
                attachment,
                style: {
                    bgShape: GraphType.RECT,
                    left: left || DEFAULT_POS,
                    top: top || DEFAULT_POS,
                },
                subType: ShapeSubType.TEXT_ELEMENT,
            };
        }
        if (showType === EShowType.CARD) {
            return {
                attachment,
                style: {
                    bgShape: GraphType.RECT,
                    left: left || DEFAULT_POS,
                    top: top || DEFAULT_POS,
                    width: DEFAULT_CARD_WIDTH,
                    height: DEFAULT_CARD_HEIGHT,
                },
                // 卡片类型更像形状而不是文本框
                subType: ShapeSubType.SHAPE,
            };
        }
    }

    private static getShapeModelExtraInfo(model: IShapeModel | IPictureModel, attachment: AttachmentInfo) {
        return model.getAttachmentManager()?.getAttachmentExtraInfo(attachment);
    }

    /**
     * 获取封面图配置
     * 视频和图片附件在切换为非图片元素时需要把图片和比例存起来，在切换回来的时候要使用
     * @param model
     * @returns
     */
    private static getPosterInfo(model: IPictureModel | IShapeModel) {
        if (isVideoElement(model) || isAttachmentImageElement(model)) {
            return {
                posterInfo: {
                    url: (model as IPictureModel).props.src,
                },
            };
        }
        return null;
    }

    /**
     * 切換形態時提示
     * @param model
     * @param showType
     * @param tipCallback
     */
    private static handleChangeTips(
        model: IShapeModel | IPictureModel | IBaseModel,
        showType: EShowType,
        tipCallback: TipCallback,
    ) {
        // 从音频的预览态切换为其他态
        let message = '';
        let type: ISnackbarProps['type'] = 'info';

        if (isAudioElement(model)) {
            const tip = new Set();
            const { animList } = model;
            animList.forEach((animModel) => {
                const { presetClass } = animModel.props;
                const { audioNumberedSlide = 0, audioRepeatCount } = animModel;
                if (presetClass === presetClassTypes.MEDIA) {
                    audioNumberedSlide > 1 && tip.add(i18n.t('跨页播放'));
                    audioRepeatCount && tip.add(i18n.t('循环播放'));
                }
            });
            if (tip.size) {
                message = i18n.t(
                    '{0}态音频暂不支持{1},效果已清除',
                    { 0: ShowTypeTrans[showType], 1: Array.from(tip).join('、') },
                );
                type = 'info';
            }
        }
        message && tipCallback(message, type);
    }
}

